import { ChangeDetectionStrategy, Component, computed, inject, signal, ViewEncapsulation } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { PermissionService, TenantService } from '@hta/auth';
import { ConfirmDialogComponent } from 'app/core/confirm-dialog/confirm-dialog.component';

/**
 * Selector de corporativo (tenant) visible en el header.
 * Siempre muestra el corporativo actual y, al pulsar, lista todos los
 * corporativos del usuario para cambiar. Fácil de identificar para el usuario.
 */
@Component({
    selector: 'app-tenant-switcher',
    templateUrl: './tenant-switcher.component.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        MatButtonModule,
        MatMenuModule,
        MatIconModule,
        MatProgressSpinnerModule,
    ],
})
export class TenantSwitcherComponent {
    private readonly tenantService = inject(TenantService);
    private readonly permissionService = inject(PermissionService);
    private readonly dialog = inject(MatDialog);

    readonly tenants = this.tenantService.tenants;
    readonly currentTenant = this.tenantService.currentTenant;
    readonly loading = this.tenantService.loading;
    readonly switching = signal(false);

    readonly currentLabel = computed(() => this.currentTenant()?.tenantName ?? 'Sin corporativo');

    isCurrent(tenantId: number): boolean {
        return this.currentTenant()?.tenantId === tenantId;
    }

    async switchTo(tenantId: number): Promise<void> {
        if (this.switching() || this.isCurrent(tenantId)) {
            return;
        }
        const ref = this.dialog.open(ConfirmDialogComponent, {
            width: '420px',
            data: {
                title: 'Cambiar de corporativo',
                message: '¿Estás seguro de que deseas cambiar de corporativo? Se perderán los cambios no guardados.',
                confirmLabel: 'Cambiar',
                cancelLabel: 'Cancelar',
                confirmColor: 'warn',
            },
        });
        const ok = await ref.afterClosed().toPromise();
        if (ok !== true) return;
        this.switching.set(true);
        this.tenantService.selectTenant(tenantId).subscribe({
            next: () => {
                this.permissionService.clearCache();
                window.location.assign('/');
            },
            error: () => this.switching.set(false),
        });
    }
}
